import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Switch,
  Tag,
  Tooltip,
  message
} from 'antd';
// Imported by path on purpose. config-overrides.js rewrites `from 'antd'`
// imports to also load antd/es/<component>/style, and antd 5 ships no style
// folder for these two (they style themselves at runtime) — so the normal
// import fails the build with "Can't resolve 'antd/es/auto-complete/style'".
import AutoComplete from 'antd/es/auto-complete';
import TimePicker from 'antd/es/time-picker';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CameraOutlined,
  CheckOutlined,
  DeleteOutlined,
  FontSizeOutlined,
  DashboardOutlined,
  PlusOutlined,
  SaveOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  FREQUENCY_OPTIONS,
  ITEM_TYPE_OPTIONS,
  WEEKDAYS,
  WEEK_OPTIONS,
  blankItem,
  blankSection,
  cid,
  countItems,
  describeRange,
  describeRule,
  toDraft
} from 'src/constants/checklists';
import { assigneeNames } from 'src/constants/checklistAccess';
import BackButton from 'src/components/views/admin/checklists/BackButton';

const { TextArea } = Input;

const ACCENT = '#2f8a4c';

const Wrap = styled.div`
  .ant-card {
    border-radius: 12px;
  }
  .ant-card-head-title {
    font-family: Nunito, sans-serif;
    font-weight: 800;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75em;
  margin-bottom: 1.25em;

  h1 {
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 22px;
    margin: 0;
    flex: 1 1 12em;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Settings stay in view while a long checklist scrolls past on desktop.
const Sticky = styled.div`
  @media (min-width: 992px) {
    position: sticky;
    top: 6em;
  }
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #5b6660;
  margin: 0.9em 0 0.35em;
  &:first-child {
    margin-top: 0;
  }
`;

const Help = styled.div`
  font-size: 12px;
  color: #7a857f;
  margin-top: 0.35em;
  line-height: 1.45;
`;

// Who a section is for, shown under its note.
const SectionAssign = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  margin: 0 0 0.5em 2.6em;
  color: #7a857f;
`;

const SectionCard = styled(Card)`
  margin-bottom: 1em;
  .ant-card-body {
    padding: 1em 1em 0.75em;
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 0.5em;

  .number {
    flex: none;
    width: 1.9em;
    height: 1.9em;
    border-radius: 50%;
    background: #1c1c1c;
    color: #fff;
    font-weight: 700;
    display: grid;
    place-items: center;
    font-size: 13px;
  }
  .title {
    flex: 1;
    font-weight: 700;
    font-size: 16px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35em;
  padding: 0.3em 0;
  border-top: 1px solid #eef1ef;

  .type {
    flex: none;
    width: 2em;
    text-align: center;
    color: ${ACCENT};
  }
  .label {
    flex: 1 1 auto;
    min-width: 0;
  }
  .tools {
    flex: none;
    display: flex;
    gap: 0.1em;
  }

  @media (max-width: 575px) {
    flex-wrap: wrap;
    .tools {
      margin-left: 2.35em;
    }
  }
`;

const ItemMeta = styled.div`
  margin: -0.1em 0 0.35em 2.35em;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3em;
  .ant-tag {
    margin: 0;
    font-size: 11px;
  }
`;

const Details = styled.div`
  margin: 0.25em 0 0.75em 2.35em;
  padding: 0.85em 1em;
  background: #f6f9f7;
  border-radius: 10px;
`;

const DayChips = ({ value = [], onChange, single = false }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
    {WEEKDAYS.map((day, index) => (
      <Tag.CheckableTag
        key={day}
        checked={value.includes(index)}
        onChange={checked => {
          if (single) return onChange(checked ? [index] : []);
          return onChange(
            checked ? [...value, index].sort() : value.filter(d => d !== index)
          );
        }}
        style={{ border: '1px solid #d9e2dc', padding: '0.15em 0.6em' }}
      >
        {day}
      </Tag.CheckableTag>
    ))}
  </div>
);

// dayjs can't parse "HH:mm" with a format string here (customParseFormat isn't
// loaded in this app), so anchor it to a date and let ISO parsing handle it.
const TimeField = ({ value, onChange, placeholder }) => (
  <TimePicker
    format="HH:mm"
    minuteStep={5}
    needConfirm={false}
    placeholder={placeholder}
    value={value ? dayjs(`2000-01-01T${value}`) : null}
    onChange={v => onChange(v ? v.format('HH:mm') : null)}
    style={{ width: '100%' }}
  />
);

// When an item applies: every run, certain weekdays, or a monthly occurrence.
const RuleEditor = ({ value, onChange }) => {
  const rule = value || {};
  const frequency = rule.frequency || 'daily';
  return (
    <>
      <Segmented
        size="small"
        value={frequency}
        onChange={f =>
          onChange(
            f === 'daily'
              ? null
              : { frequency: f, days: rule.days || [], week: rule.week || 1 }
          )
        }
        options={[
          { label: 'Every time', value: 'daily' },
          { label: 'Certain days', value: 'weekly' },
          { label: 'Monthly', value: 'monthly' }
        ]}
      />
      {frequency === 'weekly' && (
        <div style={{ marginTop: '0.5em' }}>
          <DayChips value={rule.days} onChange={days => onChange({ ...rule, days })} />
        </div>
      )}
      {frequency === 'monthly' && (
        <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.5em' }}>
          <Select
            size="small"
            value={rule.week || 1}
            options={WEEK_OPTIONS}
            onChange={week => onChange({ ...rule, week })}
            style={{ width: '6em' }}
          />
          <DayChips single value={rule.days} onChange={days => onChange({ ...rule, days })} />
        </div>
      )}
    </>
  );
};

const typeIcon = type =>
  type === 'reading' ? (
    <DashboardOutlined />
  ) : type === 'text' ? (
    <FontSizeOutlined />
  ) : (
    <CheckOutlined />
  );

// Turn pasted lines (from Word, a spreadsheet, a message) into item labels,
// dropping bullets and numbering.
const parsePastedLines = text =>
  String(text || '')
    .split(/\r?\n/)
    .map(line => line.replace(/^\s*(?:[-*•·◦▪]+|\d+[.)])\s*/, '').trim())
    .filter(Boolean);

const move = (list, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= list.length) return;
  [list[index], list[target]] = [list[target], list[index]];
};

const validate = draft => {
  const problems = [];
  if (!String(draft.name || '').trim()) problems.push('Give the checklist a name.');

  const items = draft.sections.flatMap(s =>
    String(s.title || '').trim() ? s.items.filter(i => String(i.label || '').trim()) : []
  );
  if (!items.length) problems.push('Add at least one section with at least one item.');

  draft.sections.forEach((s, si) => {
    const labelled = s.items.some(i => String(i.label || '').trim());
    if (labelled && !String(s.title || '').trim()) {
      problems.push(`Section ${si + 1} needs a title, or its items won't be saved.`);
    }
  });

  const sched = draft.schedule;
  if (sched.frequency === 'weekly' && !(sched.days || []).length) {
    problems.push('Pick at least one weekday for a weekly checklist.');
  }
  if (sched.frequency === 'monthly' && !(sched.days || []).length) {
    problems.push('Pick a weekday for a monthly checklist.');
  }
  if (sched.frequency !== 'onDemand') {
    (sched.windows || []).forEach((w, wi) => {
      if (!w.due) problems.push(`Time slot ${wi + 1} needs a "due by" time.`);
      if (w.start && w.due && w.start > w.due) {
        problems.push(`Time slot ${wi + 1} starts after it's due.`);
      }
    });
  }

  items.forEach(i => {
    if (i.type !== 'reading') return;
    const min = i.min === '' || i.min === undefined ? null : i.min;
    const max = i.max === '' || i.max === undefined ? null : i.max;
    if (min !== null && max !== null && Number(min) > Number(max)) {
      problems.push(`"${i.label}": the minimum is above the maximum.`);
    }
  });

  return problems;
};

const TemplateEditor = ({
  template,
  departments = [],
  assignees = [],
  onSave,
  onDelete,
  onClose
}) => {
  const [draft, setDraftState] = React.useState(() => toDraft(template));
  const [dirty, setDirty] = React.useState(!template._id);
  const [saving, setSaving] = React.useState(false);
  const [expanded, setExpanded] = React.useState({});
  const [pasteFor, setPasteFor] = React.useState(null);
  const [pasteText, setPasteText] = React.useState('');

  // The latest draft, readable synchronously.
  const draftRef = React.useRef(draft);
  const setDraft = next => {
    draftRef.current = next;
    setDraftState(next);
  };

  // Every edit works on a fresh copy, so nested changes can be written as plain
  // mutations. The mutation runs immediately rather than inside a state
  // updater: React 16 pools synthetic events, so `e.target.value` is gone by
  // the time a deferred updater would run.
  const change = mutate => {
    const next = JSON.parse(JSON.stringify(draftRef.current));
    mutate(next);
    setDraft(next);
    setDirty(true);
  };

  const toggleExpanded = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const handleSave = async () => {
    const problems = validate(draft);
    if (problems.length) {
      Modal.warning({
        title: 'Almost there',
        content: (
          <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
            {problems.map(p => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )
      });
      return;
    }
    setSaving(true);
    try {
      const saved = await onSave(draft);
      if (saved) {
        setDraft(toDraft(saved));
        setDirty(false);
      }
      message.success('Checklist saved');
    } catch (err) {
      message.error((err && err.message) || 'Could not save the checklist.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!dirty) return onClose();
    return Modal.confirm({
      title: 'Discard your changes?',
      content: 'You have unsaved changes to this checklist.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: onClose
    });
  };

  const addPasted = () => {
    const labels = parsePastedLines(pasteText);
    if (!labels.length) return setPasteFor(null);
    change(d => {
      const section = d.sections.find(s => s._cid === pasteFor);
      if (!section) return;
      // Replace a lone empty starter row rather than leaving it behind.
      section.items = section.items.filter(i => String(i.label || '').trim());
      labels.forEach(label => section.items.push(blankItem(label)));
    });
    setPasteFor(null);
    setPasteText('');
    return message.success(`Added ${labels.length} item${labels.length === 1 ? '' : 's'}`);
  };

  // Everyone who can be picked, plus anyone already assigned who has since lost
  // access to the site (so they still show by name and can be removed).
  const people = new Map(assignees.map(a => [a.id, a.name]));
  [draft.assignees, ...draft.sections.map(s => s.assignees)].forEach(list =>
    (list || []).forEach(a => {
      if (a && a.id && !people.has(a.id)) people.set(a.id, a.name || 'Former user');
    })
  );
  const peopleOptions = Array.from(people, ([value, label]) => ({ value, label }));
  const toPeople = ids => ids.map(id => ({ id, name: people.get(id) || '' }));
  const idsOf = list => (list || []).map(a => a.id);
  const checklistNames = assigneeNames(draft.assignees);

  const sched = draft.schedule;
  const onDemand = sched.frequency === 'onDemand';
  const itemCount = countItems(draft);

  return (
    <Wrap>
      <TopBar>
        <BackButton onClick={handleClose}>Checklists</BackButton>
        <h1>{draft.name || 'New checklist'}</h1>
        {dirty && <Tag color="orange">Unsaved</Tag>}
        {draft._id && onDelete && (
          <Popconfirm
            title="Delete this checklist?"
            description="Staff will stop seeing it. Completed checklists stay in history."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(draft._id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        )}
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          style={{ backgroundColor: '#1c1c1c' }}
        >
          Save
        </Button>
      </TopBar>

      <Row gutter={[20, 16]}>
        <Col xs={{ span: 24, order: 1 }} lg={{ span: 8, order: 2 }}>
          <Sticky>
            <Card title="Details" size="small">
              <Label>Name</Label>
              <Input
                size="large"
                value={draft.name}
                placeholder="e.g. Breakfast Buffet"
                onChange={e => change(d => (d.name = e.target.value))}
              />
              <Label>Department</Label>
              <AutoComplete
                value={draft.department}
                options={departments.map(d => ({ value: d }))}
                placeholder="e.g. Kitchen"
                onChange={value => change(d => (d.department = value))}
                filterOption={(input, option) =>
                  option.value.toLowerCase().includes(String(input).toLowerCase())
                }
                style={{ width: '100%' }}
              />
              <Label>Description</Label>
              <TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                value={draft.description}
                placeholder="Optional — shown to staff at the top"
                onChange={e => change(d => (d.description = e.target.value))}
              />
              <Row style={{ marginTop: '1em' }} gutter={8}>
                <Col span={12}>
                  <Switch
                    size="small"
                    checked={draft.active !== false}
                    onChange={v => change(d => (d.active = v))}
                  />{' '}
                  Active
                </Col>
                <Col span={12}>
                  <Switch
                    size="small"
                    checked={draft.requiresSignOff !== false}
                    onChange={v => change(d => (d.requiresSignOff = v))}
                  />{' '}
                  Sign-off
                </Col>
              </Row>
              <Help>
                Inactive checklists are hidden from staff. Sign-off asks whoever
                finishes to confirm their name.
              </Help>
              <div style={{ marginTop: '0.9em' }}>
                <Switch
                  size="small"
                  checked={draft.allowMarkAll !== false}
                  onChange={v => change(d => (d.allowMarkAll = v))}
                />{' '}
                "All good" button
              </div>
              <Help>
                Lets staff mark every remaining tick in a section as Yes in one
                tap, then change the exceptions. Photo and reading items are
                never filled in. Turn off for audits where each item must be
                checked individually.
              </Help>
            </Card>

            <Card title="Assigned to" size="small">
              <Select
                mode="multiple"
                size="large"
                allowClear
                showSearch
                optionFilterProp="label"
                value={idsOf(draft.assignees)}
                options={peopleOptions}
                placeholder="Everyone at the site"
                notFoundContent="No staff with access to this site"
                onChange={ids => change(d => (d.assignees = toPeople(ids)))}
                style={{ width: '100%' }}
              />
              <Help>
                Only these people see it on their Today list. Leave empty for
                everyone at the site. Sections can also be handed to other people
                below. Managers and admins always see every checklist.
              </Help>
            </Card>

            <Card title="Schedule" size="small">
              <Segmented
                block
                value={sched.frequency}
                options={FREQUENCY_OPTIONS}
                onChange={value =>
                  change(d => {
                    d.schedule.frequency = value;
                    if (value === 'monthly') {
                      d.schedule.days = (d.schedule.days || []).slice(0, 1);
                      d.schedule.week = d.schedule.week || 1;
                    }
                  })
                }
              />

              {sched.frequency === 'weekly' && (
                <>
                  <Label>On these days</Label>
                  <DayChips
                    value={sched.days || []}
                    onChange={days => change(d => (d.schedule.days = days))}
                  />
                </>
              )}

              {sched.frequency === 'monthly' && (
                <>
                  <Label>On the</Label>
                  <div style={{ display: 'flex', gap: '0.5em', flexWrap: 'wrap' }}>
                    <Select
                      value={sched.week || 1}
                      options={WEEK_OPTIONS}
                      onChange={week => change(d => (d.schedule.week = week))}
                      style={{ width: '6em' }}
                    />
                    <DayChips
                      single
                      value={sched.days || []}
                      onChange={days => change(d => (d.schedule.days = days))}
                    />
                  </div>
                </>
              )}

              {onDemand ? (
                <Help>
                  Staff start this checklist themselves whenever it's needed — for
                  example once per room.
                </Help>
              ) : (
                <>
                  <Label>Time slots</Label>
                  {(sched.windows || []).map((w, wi) => (
                    <Row key={w._cid || wi} gutter={6} style={{ marginBottom: '0.4em' }} align="middle">
                      <Col span={9}>
                        <Input
                          value={w.label}
                          placeholder="Label"
                          onChange={e =>
                            change(d => (d.schedule.windows[wi].label = e.target.value))
                          }
                        />
                      </Col>
                      <Col span={6}>
                        <TimeField
                          value={w.start}
                          placeholder="From"
                          onChange={v => change(d => (d.schedule.windows[wi].start = v))}
                        />
                      </Col>
                      <Col span={6}>
                        <TimeField
                          value={w.due}
                          placeholder="Due"
                          onChange={v => change(d => (d.schedule.windows[wi].due = v))}
                        />
                      </Col>
                      <Col span={3}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => change(d => d.schedule.windows.splice(wi, 1))}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      change(d =>
                        d.schedule.windows.push({ _cid: cid(), key: '', label: '', start: null, due: null })
                      )
                    }
                  >
                    Add time slot
                  </Button>
                  <Help>
                    One checklist is created per slot — e.g. separate Lunch and
                    Dinner runs. With no slots it has no deadline and never shows
                    as overdue.
                  </Help>
                </>
              )}
            </Card>

            <Card title="Ask when starting" size="small">
              {(draft.runFields || []).map((field, fi) => (
                <Row key={field._cid || fi} gutter={6} align="middle" style={{ marginBottom: '0.4em' }}>
                  <Col flex="auto">
                    <Input
                      value={field.label}
                      placeholder="e.g. Room number"
                      onChange={e => change(d => (d.runFields[fi].label = e.target.value))}
                    />
                  </Col>
                  <Col flex="none">
                    <Tooltip title="Required">
                      <Switch
                        size="small"
                        checked={field.required !== false}
                        onChange={v => change(d => (d.runFields[fi].required = v))}
                      />
                    </Tooltip>
                  </Col>
                  <Col flex="none">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => change(d => d.runFields.splice(fi, 1))}
                    />
                  </Col>
                </Row>
              ))}
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  change(d => d.runFields.push({ _cid: cid(), label: '', required: true }))
                }
              >
                Add question
              </Button>
              <Help>Asked once before the checklist begins, like which room it's for.</Help>
            </Card>
          </Sticky>
        </Col>

        <Col xs={{ span: 24, order: 2 }} lg={{ span: 16, order: 1 }}>
          <div style={{ color: '#5b6660', marginBottom: '0.75em' }}>
            {draft.sections.length} section{draft.sections.length === 1 ? '' : 's'} ·{' '}
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </div>

          {draft.sections.map((section, si) => (
            <SectionCard key={section._cid || section.key} size="small">
              <SectionHead>
                <span className="number">{si + 1}</span>
                <Input
                  className="title"
                  variant="borderless"
                  value={section.title}
                  placeholder="Section title, e.g. Bar"
                  onChange={e => change(d => (d.sections[si].title = e.target.value))}
                  style={{ fontWeight: 700, fontSize: 16, paddingLeft: 4 }}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={si === 0}
                  onClick={() => change(d => move(d.sections, si, -1))}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={si === draft.sections.length - 1}
                  onClick={() => change(d => move(d.sections, si, 1))}
                />
                <Popconfirm
                  title="Remove this section and its items?"
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => change(d => d.sections.splice(si, 1))}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </SectionHead>

              <Input
                size="small"
                variant="borderless"
                value={section.note}
                placeholder="Optional note shown above these items"
                onChange={e => change(d => (d.sections[si].note = e.target.value))}
                style={{ color: '#7a857f', marginBottom: '0.4em', paddingLeft: '2.6em' }}
              />
              <SectionAssign>
                <TeamOutlined />
                <Select
                  mode="multiple"
                  size="small"
                  variant="borderless"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={idsOf(section.assignees)}
                  options={peopleOptions}
                  placeholder={
                    checklistNames
                      ? `Same as checklist (${checklistNames})`
                      : 'Same as checklist (everyone)'
                  }
                  onChange={ids => change(d => (d.sections[si].assignees = toPeople(ids)))}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </SectionAssign>

              {section.items.map((item, ii) => {
                const id = item._cid || item.key;
                const open = Boolean(expanded[id]);
                const rule = describeRule(item.when);
                return (
                  <React.Fragment key={id}>
                    <ItemRow>
                      <span className="type">{typeIcon(item.type)}</span>
                      <Input
                        className="label"
                        variant="borderless"
                        value={item.label}
                        placeholder="What needs checking?"
                        onChange={e =>
                          change(d => (d.sections[si].items[ii].label = e.target.value))
                        }
                        onPressEnter={() =>
                          change(d => d.sections[si].items.splice(ii + 1, 0, blankItem()))
                        }
                      />
                      <span className="tools">
                        <Tooltip title={item.photoRequired ? 'Photo required' : 'Require a photo'}>
                          <Button
                            type={item.photoRequired ? 'primary' : 'text'}
                            size="small"
                            icon={<CameraOutlined />}
                            onClick={() =>
                              change(
                                d =>
                                  (d.sections[si].items[ii].photoRequired = !item.photoRequired)
                              )
                            }
                            style={item.photoRequired ? { backgroundColor: ACCENT } : undefined}
                          />
                        </Tooltip>
                        <Tooltip
                          title={
                            item.critical
                              ? 'Critical: no N/A, never ticked by "All good", failures flagged'
                              : 'Mark as critical'
                          }
                        >
                          <Button
                            type={item.critical ? 'primary' : 'text'}
                            size="small"
                            icon={<WarningOutlined />}
                            onClick={() =>
                              change(d => (d.sections[si].items[ii].critical = !item.critical))
                            }
                            style={item.critical ? { backgroundColor: '#c8442b' } : undefined}
                            aria-pressed={Boolean(item.critical)}
                            aria-label="Critical item"
                          />
                        </Tooltip>
                        <Tooltip title="More options">
                          <Button
                            type={open ? 'default' : 'text'}
                            size="small"
                            icon={<SettingOutlined />}
                            onClick={() => toggleExpanded(id)}
                          />
                        </Tooltip>
                        <Button
                          type="text"
                          size="small"
                          icon={<ArrowUpOutlined />}
                          disabled={ii === 0}
                          onClick={() => change(d => move(d.sections[si].items, ii, -1))}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<ArrowDownOutlined />}
                          disabled={ii === section.items.length - 1}
                          onClick={() => change(d => move(d.sections[si].items, ii, 1))}
                        />
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => change(d => d.sections[si].items.splice(ii, 1))}
                        />
                      </span>
                    </ItemRow>

                    {!open &&
                      (item.type === 'reading' || item.responsible || rule || item.standard) && (
                        <ItemMeta>
                          {item.type === 'reading' && (
                            <Tag color="blue">Reading · {describeRange(item)}</Tag>
                          )}
                          {item.type === 'text' && <Tag>Written answer</Tag>}
                          {item.responsible && <Tag>{item.responsible}</Tag>}
                          {rule && <Tag color="purple">{rule}</Tag>}
                          {item.standard && (
                            <Tag style={{ maxWidth: '22em', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.standard}
                            </Tag>
                          )}
                        </ItemMeta>
                      )}

                    {open && (
                      <Details>
                        <Label>Answer type</Label>
                        <Segmented
                          size="small"
                          value={item.type || 'tick'}
                          options={ITEM_TYPE_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
                          onChange={type => change(d => (d.sections[si].items[ii].type = type))}
                        />
                        <Help>{(ITEM_TYPE_OPTIONS.find(o => o.value === (item.type || 'tick')) || {}).hint}</Help>

                        {item.type === 'reading' && (
                          <>
                            <Label>Acceptable range</Label>
                            <Row gutter={8}>
                              <Col span={8}>
                                <InputNumber
                                  value={item.min}
                                  placeholder="Min"
                                  onChange={v => change(d => (d.sections[si].items[ii].min = v))}
                                  style={{ width: '100%' }}
                                />
                              </Col>
                              <Col span={8}>
                                <InputNumber
                                  value={item.max}
                                  placeholder="Max"
                                  onChange={v => change(d => (d.sections[si].items[ii].max = v))}
                                  style={{ width: '100%' }}
                                />
                              </Col>
                              <Col span={8}>
                                <Input
                                  value={item.unit}
                                  placeholder="Unit, e.g. °C"
                                  onChange={e =>
                                    change(d => (d.sections[si].items[ii].unit = e.target.value))
                                  }
                                />
                              </Col>
                            </Row>
                            <div style={{ marginTop: '0.5em' }}>
                              <Switch
                                size="small"
                                checked={Boolean(item.strict)}
                                onChange={v => change(d => (d.sections[si].items[ii].strict = v))}
                              />{' '}
                              Limits are exclusive{' '}
                              <span style={{ color: '#7a857f' }}>
                                (below 4°C, not 4°C or lower)
                              </span>
                            </div>
                            <Help>
                              Out-of-range readings show red and need a corrective
                              action before the checklist can be finished.
                            </Help>
                          </>
                        )}

                        <Label>Standard</Label>
                        <TextArea
                          autoSize={{ minRows: 1, maxRows: 4 }}
                          value={item.standard}
                          placeholder="What 'done' looks like — shown to staff under the item"
                          onChange={e =>
                            change(d => (d.sections[si].items[ii].standard = e.target.value))
                          }
                        />

                        <Row gutter={12}>
                          <Col xs={24} sm={12}>
                            <Label>Responsible</Label>
                            <Input
                              value={item.responsible}
                              placeholder="e.g. Head Chef"
                              onChange={e =>
                                change(d => (d.sections[si].items[ii].responsible = e.target.value))
                              }
                            />
                          </Col>
                          <Col xs={24} sm={12}>
                            <Label>Include this item</Label>
                            <RuleEditor
                              value={item.when}
                              onChange={when =>
                                change(d => {
                                  if (when) d.sections[si].items[ii].when = when;
                                  else delete d.sections[si].items[ii].when;
                                })
                              }
                            />
                          </Col>
                        </Row>
                      </Details>
                    )}
                  </React.Fragment>
                );
              })}

              <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.6em', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => change(d => d.sections[si].items.push(blankItem()))}
                >
                  Add item
                </Button>
                <Button
                  size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={() => {
                    setPasteText('');
                    setPasteFor(section._cid);
                  }}
                >
                  Paste a list
                </Button>
              </div>
            </SectionCard>
          ))}

          <Button
            block
            size="large"
            icon={<PlusOutlined />}
            onClick={() => change(d => d.sections.push(blankSection()))}
            style={{ borderStyle: 'dashed', marginBottom: '2em' }}
          >
            Add section
          </Button>
        </Col>
      </Row>

      <Modal
        open={Boolean(pasteFor)}
        title="Paste a list of items"
        okText="Add items"
        onOk={addPasted}
        onCancel={() => setPasteFor(null)}
      >
        <p style={{ color: '#5b6660' }}>
          One item per line. Bullets and numbering are removed — paste straight
          from Word or a spreadsheet.
        </p>
        <TextArea
          autoSize={{ minRows: 6, maxRows: 14 }}
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          placeholder={'Placemat\nSide plate with folded napkin\nPolished cutlery'}
        />
        <Help>{parsePastedLines(pasteText).length} items detected</Help>
      </Modal>
    </Wrap>
  );
};

export default TemplateEditor;
